import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import '../styles/HamburgerMenu.css';

/**
 * Menü-Reihenfolge (bewusst so festgelegt, siehe Absprache mit dem Nutzer):
 * Einkauf -> To-Do -> Rezepte -> Lesezeichen -> Kalender -> Kategorien ->
 * Archiv -> Einstellungen (Allgemein/Profil/Themes/Import/Export/Support/
 * Datenschutz/Version) -> Logout (Logout in der Liste des Nutzers nicht
 * explizit genannt, bleibt aber als notwendige Aktion ganz unten stehen).
 * Alle Punkte außer "Einstellungen" sind Direkt-Klick-Buttons ohne eigenes
 * Untermenü (vorher hatten Lesezeichen/Einkauf/Rezepte je ein Untermenü mit
 * nur einem einzigen Eintrag - das wurde dafür bewusst abgeflacht).
 */
const HamburgerMenu = ({
  onModeChange,
  forceOnlineMode,
  apiAvailable,
  onImportClick,
  premiumUser = false,
  bookmarks = [],
  onOpenPanel,
  onOpenRecipePanel
}) => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden'; // Prevent scroll
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleLogout = () => {
    if (window.confirm(t('logoutConfirm') || 'Are you sure you want to logout?')) {
      logout();
      setIsOpen(false);
    }
  };

  // toggleOnlineMode function removed as it's not used

  const handleMenuClick = (action) => {
    setIsOpen(false);
    setSettingsOpen(false);

    // Recipe actions - open as overlays
    if (action === 'recipes') {
      onOpenRecipePanel && onOpenRecipePanel('recipes');
      return;
    }
    if (action === 'quick-shopping-list') {
      onOpenRecipePanel && onOpenRecipePanel('quick-shopping-list');
      return;
    }
    if (action === 'todo') {
      onOpenRecipePanel && onOpenRecipePanel('quick-todo-list');
      return;
    }

    // Actions that open in dynamic content area
    if (action === 'import') {
      onOpenPanel('import');
      return;
    }
    if (action === 'categories') {
      onOpenPanel('categories');
      return;
    }
    if (action === 'export') {
      onOpenPanel('export');
      return;
    }
    if (action === 'archive') {
      onOpenPanel('archive');
      return;
    }
    if (action === 'themes') {
      onOpenPanel('themes');
      return;
    }
    if (action === 'privacy') {
      onOpenPanel('privacy');
      return;
    }
    if (action === 'settings') {
      onOpenPanel('settings');
      return;
    }
    if (action === 'profile') {
      onOpenPanel('profile');
      return;
    }
    if (action === 'support') {
      onOpenPanel('support');
      return;
    }
    if (action === 'version') {
      onOpenPanel('version');
      return;
    }
    if (action === 'bookmarks') {
      onOpenPanel('bookmarks');
      return;
    }
    if (action === 'reminders') {
      onOpenPanel('reminders');
      return;
    }
  };

  return (
    <>
      {/* Hamburger Button */}
      <button
        className={`hamburger-button ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Menu"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      {/* Overlay */}
      {isOpen && <div className="menu-overlay" onClick={() => setIsOpen(false)} />}

      {/* Menu */}
      <div className={`hamburger-menu ${isOpen ? 'open' : ''}`} ref={menuRef}>
        {/* Close Button for Mobile */}
        {isOpen && (
          <button
            className="menu-close-btn"
            onClick={() => setIsOpen(false)}
            aria-label="Close menu"
          >
            ✕
          </button>
        )}
        {/* User Info Header */}
        <div className="menu-header">
          <div className="user-details">
            <div className="user-name">{user?.name || user?.email?.split('@')[0] || 'Guest'}</div>
            <div className="user-email">{user?.email || 'Not logged in'}</div>
            <div className="user-status">
              <div className="connection-status">
                <span className={`status-indicator ${apiAvailable ? 'online' : 'offline'}`}></span>
                <span className="status-text">
                  {apiAvailable ? 'Online' : 'Wartungsmodus'}
                </span>
              </div>
              {premiumUser && <span className="premium-indicator">🧪 Beta</span>}
            </div>
          </div>
        </div>

        {/* Menu Items - alle Punkte bis auf "Einstellungen" sind flache
            Direkt-Klick-Buttons, exakte Reihenfolge wie mit dem Nutzer
            abgesprochen */}
        <div className="menu-items">
          <button className="menu-item" onClick={() => handleMenuClick('quick-shopping-list')} data-testid="quick-shopping-list-btn">
            <span className="menu-icon">🛒</span>
            <span className="menu-label">{t('menu.shoppingList') || 'Einkauf'}</span>
          </button>

          <button className="menu-item" onClick={() => handleMenuClick('todo')}>
            <span className="menu-icon">✅</span>
            <span className="menu-label">{t('menu.todo') || 'To-Do'}</span>
          </button>

          <button className="menu-item" onClick={() => handleMenuClick('recipes')}>
            <span className="menu-icon">📖</span>
            <span className="menu-label">{t('menu.recipes') || 'Rezepte'}</span>
          </button>

          <button className="menu-item" onClick={() => handleMenuClick('bookmarks')}>
            <span className="menu-icon">📚</span>
            <span className="menu-label">{t('menu.bookmarks') || 'Lesezeichen'}</span>
          </button>

          <button className="menu-item" onClick={() => handleMenuClick('reminders')}>
            <span className="menu-icon">📅</span>
            <span className="menu-label">{t('menu.calendar') || 'Kalender'}</span>
          </button>

          <button className="menu-item" onClick={() => handleMenuClick('categories')}>
            <span className="menu-icon">🗂️</span>
            <span className="menu-label">{t('categories') || 'Kategorien'}</span>
          </button>

          <button className="menu-item" onClick={() => handleMenuClick('archive')}>
            <span className="menu-icon">🗄️</span>
            <span className="menu-label">{t('menu.dataArchive') || 'Archiv'}</span>
          </button>
        </div>

        {/* Footer Actions */}
        <div className="menu-footer">
          {/* Einstellungen mit Untermenü: Allgemein, Profil, Themes, Import,
              Export, Support, Datenschutz, Version */}
          <div className="settings-menu">
            <button
              className={`menu-item secondary ${settingsOpen ? 'expanded' : ''}`}
              onClick={() => setSettingsOpen(!settingsOpen)}
            >
              <span className="menu-icon">⚙️</span>
              <span className="menu-label">{t('settings')}</span>
              <span className="expand-icon">{settingsOpen ? '▼' : '▶'}</span>
            </button>

            {settingsOpen && (
              <div className="submenu">
                <button className="submenu-item" onClick={() => handleMenuClick('settings')}>
                  <span className="menu-icon">🔧</span>
                  <span className="menu-label">{t('settings.general') || 'Allgemein'}</span>
                </button>
                <button className="submenu-item" onClick={() => handleMenuClick('profile')}>
                  <span className="menu-icon">👤</span>
                  <span className="menu-label">{t('menu.profile') || 'Profil'}</span>
                </button>
                <button className="submenu-item" onClick={() => handleMenuClick('themes')}>
                  <span className="menu-icon">🎨</span>
                  <span className="menu-label">{t('themes')}</span>
                </button>
                <button className="submenu-item" onClick={() => handleMenuClick('import')}>
                  <span className="menu-icon">📥</span>
                  <span className="menu-label">Import</span>
                </button>
                <button className="submenu-item" onClick={() => handleMenuClick('export')}>
                  <span className="menu-icon">📤</span>
                  <span className="menu-label">Export</span>
                </button>
                <button className="submenu-item" onClick={() => handleMenuClick('support')}>
                  <span className="menu-icon">🛟</span>
                  <span className="menu-label">{t('support')}</span>
                </button>
                <button className="submenu-item" onClick={() => handleMenuClick('privacy')}>
                  <span className="menu-icon">🔐</span>
                  <span className="menu-label">{t('privacy') || 'Datenschutz'}</span>
                </button>
                <button className="submenu-item" onClick={() => handleMenuClick('version')}>
                  <span className="menu-icon">🏷️</span>
                  <span className="menu-label">{t('menu.version') || 'Version'}</span>
                </button>
              </div>
            )}
          </div>

          <button className="menu-item logout" onClick={handleLogout}>
            <span className="menu-icon">🚪</span>
            <span className="menu-label">{t('logout')}</span>
          </button>
        </div>

        {/* Beta */}
        {!premiumUser && (
          <div className="menu-section premium-section">
            <button className="upgrade-button">
              🧪 {t('upgradeBeta') || 'Beta testen'}
            </button>
          </div>
        )}
      </div>

    </>
  );
};

export default HamburgerMenu;
